import Phaser from 'phaser';
import { RENDER_SCALE } from '../renderScale';
import { scartoOrologio, tempoRestante, ancoraAttivo } from '../orologioServer';
import io from 'socket.io-client';
import Synthesizer from '../Synthesizer';
import { getToken } from '../../lib/authClient';
import { formatSolLabel } from '../format';
import { getSolPrice, prezzoSolCorrente } from '../../lib/solPrice';
import { boostAttivo } from '../boostInput';
// L'HUD dei power-up (countdown + bag) e' disegnato dalla UIScene, che non subisce
// lo zoom del Vision; qui non servono piu' le costanti di layout della leaderboard.

// --- PROPORZIONI SKIN (uniche per tutte le skin a immagine) ---
// Diametro visibile = radius * fattore. La testa e' leggermente piu'
// grande del corpo; il corpo ha un piccolo overlap per non lasciare buchi
// tra un segmento e l'altro; la coda e' un filo piu' lunga.
// NB: presuppone immagini sorgente QUADRATE con il disegno centrato che
// riempie la stessa frazione del canvas in ogni skin (vedi script di
// normalizzazione). Con canvas quadrati width === height, quindi niente
// schiacciamento su immagini alte/strette.
// Su touch il puntatore non "mira": ci pensa il joystick. Senza questa distinzione
// ogni dito appoggiato sulla mappa sterzava e faceva partire il boost.
const IS_TOUCH = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const SKIN_HEAD_FACTOR = 2.5;
const SKIN_BODY_FACTOR = 2.2;
const SKIN_TAIL_FACTOR = 2.6;

// --- CIBO BASE (quello che da' solo massa) ---
// IMPORTANTE: il gioco puo' girare col renderer CANVAS (quando la GPU/WebGL non e'
// disponibile), e in Canvas Phaser IGNORA setTint -> il cibo, che era una texture
// bianca tinta, restava BIANCO. Percio' il colore va COTTO dentro la texture, senza
// tint: una texture (nucleo pieno colorato + alone morbido) per ciascun colore.
// I colori del cibo sono pochi (palette del server), quindi le texture si cachano
// e restano poche draw call. Funziona identico in Canvas e in WebGL.
const FOOD_TEX_PREFIX = 'food_tex_';

// Diametro a schermo del cibo base. Non e' uno solo: ogni particella pesca una
// misura in questo intervallo, cosi' il campo non sembra stampato a stampino.
// La misura dipende dall'id (non da Math.random) per non cambiare se lo sprite
// viene ricreato, ad esempio dopo un respawn.
const FOOD_DRAW_MIN = 16;
const FOOD_DRAW_MAX = 26;

// Respiro del cibo base: ampiezza in frazione della misura e periodo, anch'essi
// leggermente diversi da una particella all'altra perche' il campo pulsi in
// modo sparso invece che tutto all'unisono.
const FOOD_PULSE_AMPLITUDE = 0.12;
const FOOD_PULSE_MS_MIN = 1300;
const FOOD_PULSE_MS_MAX = 2300;

// Numero stabile in [0,1) ricavato dall'id: serve a dare a ogni particella la
// sua misura e la sua fase senza doverle memorizzare da qualche parte.
const hash01 = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
};

// --- POWER-UP ---
// Unica fonte di verita' per aspetto e colore di ogni tipo: la usano sia i
// pickup sulla mappa (alone + icona) sia l'HUD della tasca in UIScene.
// I tipi che hanno una PNG dedicata usano quella; gli altri disegnano un
// disco colorato con l'emoji sopra.
const POWERUP_STYLES = {
  VISION: { color: 0x00FFFF, label: 'VISION', imageKey: 'pu_vision', icon: '👁' },
  SPEED: { color: 0xFF0000, label: 'SPEED', imageKey: 'pu_speed', icon: '⚡' },
  MULTIPLIER: { color: 0xFFD700, label: '2X SCORE', imageKey: 'pu_multiplier', icon: '✖' },
  MAGNET: { color: 0xFF00FF, label: 'MAGNET', imageKey: 'pu_magnet', icon: '🧲' },
  RADAR: { color: 0x39FF14, label: 'RADAR', imageKey: 'pu_radar', icon: '📡' },
  // Il nucleo della frenesia. Bianco perche' e' tutti gli altri insieme, e
  // perche' e' l'unico colore che nessun power-up occupa. Niente immagine
  // ancora: il disegno di ripiego (cerchio + icona) e' gia' quello giusto per
  // un oggetto che deve gridare "non sono un power-up normale".
  FRENZY: { color: 0xFFFFFF, label: 'FRENZY', icon: '🌀' },
};

// Alone luminoso dei power-up: una texture radiale per colore, additiva.
const AURA_TEX_PREFIX = 'pu_aura_';

// --- SFONDO ESAGONALE ---
// Il tile del nido d'ape DEVE essere una potenza di due: il TileSprite di Phaser
// ridisegna in un canvas POT le texture che non lo sono, stirandole a dismisura
// (gli esagoni diventavano enormi). Vedi hexTileTextureKey.
// --- COLORE DELL'ARENA ---
// Ogni stanza ha il suo colore, lo stesso con cui e' segnata nel white paper
// (🟢 THE TRENCHES, 🟡 THE WARZONE, 🔴 HIGH COMMAND): entrando si deve capire
// dove si e' senza leggere niente.
//
// `griglia` e' il tratto degli esagoni, gia' con la sua trasparenza: a piena
// opacita' il reticolo ruberebbe l'occhio ai serpenti, che sono la cosa da
// guardare. `fondo` e' quasi nero, con dentro un soffio dello stesso colore —
// e' quello che fa sembrare l'arena tinta invece che solo rigata.
// `accento` e' il neon della stanza: lo usano la griglia e l'emblema al centro.
// `griglia` porta gia' la sua trasparenza perche' a piena opacita' il reticolo
// ruberebbe l'occhio ai serpenti, che sono la cosa da guardare. `fondo` e'
// quasi nero col soffio del colore dentro, `core` e' il riempimento
// dell'esagono centrale dell'emblema: stessa tinta, ancora piu' spenta.
//
// `peso` corregge una cosa che i numeri da soli non dicono: a parita' di
// opacita' il rosso RISULTA molto piu' spento. Ha poca luminanza percepita —
// l'occhio ci vede meno del verde a parita' di intensita' — e composto sul
// fondo quasi nero spariva. Misurato sul colore gia' composto
// (0.2126·R + 0.7152·G + 0.0722·B): verde 42, giallo 44, rosso **18**, cioe'
// meno della meta'. Moltiplicando le trasparenze per 1,6 il rosso sale a 35 e
// le tre arene si leggono con lo stesso peso.
//
// Vale sia per la griglia sia per l'emblema, e per lo stesso motivo: se un
// giorno si toccano questi colori, si rimisura con quella formula.
const COLORI_ARENA = {
  1: { nome: 'THE TRENCHES', accento: '#39FF14', fondo: '#0a0e0a', core: '#061006', peso: 1 },
  2: { nome: 'THE WARZONE', accento: '#FFD700', fondo: '#0e0d08', core: '#100e04', peso: 1 },
  3: { nome: 'HIGH COMMAND', accento: '#FF4444', fondo: '#0e0808', core: '#100606', peso: 1.6 },
};

// Trasparenza dei tratti della griglia PRIMA del peso dell'arena.
const ALFA_GRIGLIA = 0.16;

const daHex = (css) => parseInt(css.slice(1), 16);
const aRgb = (css) => [1, 3, 5].map((i) => parseInt(css.slice(i, i + 2), 16));

// Il tier 1 e' anche il ripiego: se arrivasse un numero che non conosciamo, si
// gioca in verde invece che su uno sfondo trasparente.
export const coloriArena = (tier) => {
  const c = COLORI_ARENA[tier] || COLORI_ARENA[1];
  const [r, g, b] = aRgb(c.accento);
  return {
    ...c,
    // Pronti all'uso: Phaser vuole gli interi, il canvas della griglia le
    // stringhe. Derivati da `accento` invece che scritti a mano, cosi' non
    // possono divergere.
    accentoInt: daHex(c.accento),
    coreInt: daHex(c.core),
    griglia: `rgba(${r}, ${g}, ${b}, ${(ALFA_GRIGLIA * c.peso).toFixed(3)})`,
  };
};

// Trasparenza di un elemento dell'emblema, corretta col peso dell'arena.
// Oltre 1 non si va: un alpha maggiore di 1 non e' piu' opaco, e' solo
// sbagliato.
export const alfaArena = (colori, alfa) => Math.min(1, alfa * (colori.peso || 1));

const HEX_TILE = 512;
// Periodi del reticolo contenuti nel tile. Il rapporto ideale sarebbe
// orizzontale/verticale = √3; 4/7 lo approssima all'1%, quindi con un tile
// quadrato gli esagoni risultano regolari a occhio.
const HEX_PERIODS_X = 4;
const HEX_PERIODS_Y = 7;

// Utile fuori da qui (UIScene) per tinte e nomi coerenti.
export const powerupStyle = (type) => POWERUP_STYLES[type] || { color: 0xFFFFFF, label: type, icon: '?' };
export const hexColor = (int) => '#' + int.toString(16).padStart(6, '0').toUpperCase();

// Le immagini dentro public/assets NON hanno l'hash nel nome (l'hash lo mette
// webpack, ma solo ai file che passano da import). Quando ne cambia una,
// l'URL resta identico: il browser serve quella che ha gia' in cache, e
// nginx non aiuta perche' senza Cache-Control esplicito applica la sua
// euristica e la tiene per ore. Il giocatore vede una skin vecchia e non c'e'
// modo di accorgersene guardando il codice.
//
// La versione entra nell'URL e rompe la cache. VA ALZATA DI UNO ogni volta che
// si sostituisce un file dentro public/assets.
const VERSIONE_ASSET = 2;
const asset = (nome) => `/assets/${nome}.png?v=${VERSIONE_ASSET}`;

// Skin "a immagine" (head/body/tail come texture). Chi non e' qui usa il sistema
// procedurale a colore. Aggiungere una nuova skin immagine = una voce qui + i load
// in preload() + la voce nella Skin Vault (LandingPage).
const IMAGE_SKINS = [
  'OGskeleton', 'BinarySnake', 'singularity', 'Cybersnake',
  'Magmacore', 'Hexcore', 'Nullsector', 'Vexelprime', 'Blackhole', 'Datastream',
  'Goldenbullet', 'Cybercar',
];

// Ritocchi per-skin: moltiplicatore sul diametro (1 = nessun ritocco).
// Serve quando il disegno di UNA skin "pesa" visivamente piu' o meno delle
// altre, senza cambiare i fattori globali ne' rifare le immagini.
const SKIN_TWEAKS = {
  Cybersnake: { body: 0.8, tail: 0.8 }, // corpo piu' piccolo
  // I numeri qui sotto sono tarati sulle proporzioni di Blackhole, che e'
  // quella approvata: testa ~1.68r, corpo ~1.45r, base della coda ~1.40r.
  // Vanno rifatti se si ridisegna uno dei pezzi, perche' dipendono da quanto
  // il disegno riempie il suo riquadro.
  Hexcore: { head: 0.88, body: 0.94, tail: 0.67 },
  Nullsector: { tail: 0.74 },   // la coda arrivava piu' larga del corpo
  // Il body riempie il 91% del riquadro, la coda solo il 41%: presa a scala uno
  // l'attacco della coda era la meta' del corpo e si vedeva lo scalino.
  Vexelprime: { body: 0.72, tail: 1.55 },
  Datastream: { head: 0.87, body: 0.81 },
};

function skinTweak(skin, part) {
  return (SKIN_TWEAKS[skin] && SKIN_TWEAKS[skin][part]) || 1;
}

// Skin dal nome texture, es. "Cybersnake_body" -> "Cybersnake".
function skinOf(sprite) {
  const key = sprite.texture && sprite.texture.key ? sprite.texture.key : '';
  return key.replace(/_(head|body|tail)$/, '');
}

// Ogni quanti punti della storia si disegna un segmento.
//
// I punti distano fra loro quanto il serpente si muove in un tick (~7 px), e il
// segmento e' un'immagine larga `radius * SKIN_BODY_FACTOR * ritocco`. Con un
// passo fisso di 3 gli sprite corti non si toccavano piu' e fra un segmento e
// l'altro si vedeva il buco: succedeva alle skin con il ritocco sotto 1, cioe'
// proprio quelle assottigliate a mano (il ritocco scala l'immagine intera,
// quindi assottigliare accorcia anche).
//
// Qui il passo si ricava dalla larghezza vera dello sprite, chiedendo almeno
// meta' sovrapposizione. Il tetto resta 3 e il minimo e' 2, cosi' le skin che
// gia' andavano bene non cambiano di una virgola.
function passoSegmenti(skin, radius, distanzaPunti) {
  const larghezza = radius * SKIN_BODY_FACTOR * skinTweak(skin, 'body');
  const passo = Math.floor((larghezza * 0.5) / Math.max(distanzaPunti, 0.1));
  return Math.max(2, Math.min(3, passo));
}

// Scala uniforme che porta una texture (di lato maxDim) al diametro voluto.
function skinScaleFor(sprite, newRadius, factor) {
  const maxDim = Math.max(sprite.width || 100, sprite.height || 100);
  return (newRadius * factor) / maxDim;
}

export default class MainScene extends Phaser.Scene {
  constructor() {
    super('MainScene');
  }

  preload() {
    // Skin Images (Gestite dal Dev)
    this.load.image('OGskeleton', asset('OGskeleton'));
    this.load.image('OGskeleton_head', asset('OGskeleton_head'));
    this.load.image('OGskeleton_body', asset('OGskeleton_body'));
    this.load.image('OGskeleton_tail', asset('OGskeleton_tail'));

    this.load.image('BinarySnake', asset('BinarySnake'));
    this.load.image('BinarySnake_head', asset('BinarySnake_head'));
    this.load.image('BinarySnake_body', asset('BinarySnake_body'));
    this.load.image('BinarySnake_tail', asset('BinarySnake_tail'));

    this.load.image('singularity', asset('singularity'));
    this.load.image('singularity_head', asset('singularity_head'));
    this.load.image('singularity_body', asset('singularity_body'));
    this.load.image('singularity_tail', asset('singularity_tail'));

    this.load.image('Cybersnake', asset('Cybersnake'));
    this.load.image('Cybersnake_head', asset('Cybersnake_head'));
    this.load.image('Cybersnake_body', asset('Cybersnake_body'));
    this.load.image('Cybersnake_tail', asset('Cybersnake_tail'));

    this.load.image('Magmacore', asset('Magmacore'));
    this.load.image('Magmacore_head', asset('Magmacore_head'));
    this.load.image('Magmacore_body', asset('Magmacore_body'));
    this.load.image('Magmacore_tail', asset('Magmacore_tail'));

    this.load.image('Hexcore', asset('Hexcore'));
    this.load.image('Hexcore_head', asset('Hexcore_head'));
    this.load.image('Hexcore_body', asset('Hexcore_body'));
    this.load.image('Hexcore_tail', asset('Hexcore_tail'));

    this.load.image('Nullsector', asset('Nullsector'));
    this.load.image('Nullsector_head', asset('Nullsector_head'));
    this.load.image('Nullsector_body', asset('Nullsector_body'));
    this.load.image('Nullsector_tail', asset('Nullsector_tail'));

    this.load.image('Vexelprime', asset('Vexelprime'));
    this.load.image('Vexelprime_head', asset('Vexelprime_head'));
    this.load.image('Vexelprime_body', asset('Vexelprime_body'));
    this.load.image('Vexelprime_tail', asset('Vexelprime_tail'));

    this.load.image('Blackhole', asset('Blackhole'));
    this.load.image('Blackhole_head', asset('Blackhole_head'));
    this.load.image('Blackhole_body', asset('Blackhole_body'));
    this.load.image('Blackhole_tail', asset('Blackhole_tail'));

    this.load.image('Datastream', asset('Datastream'));
    this.load.image('Datastream_head', asset('Datastream_head'));
    this.load.image('Datastream_body', asset('Datastream_body'));
    this.load.image('Datastream_tail', asset('Datastream_tail'));

    this.load.image('Goldenbullet', asset('Goldenbullet'));
    this.load.image('Goldenbullet_head', asset('Goldenbullet_head'));
    this.load.image('Goldenbullet_body', asset('Goldenbullet_body'));
    this.load.image('Goldenbullet_tail', asset('Goldenbullet_tail'));

    this.load.image('Cybercar', asset('Cybercar'));
    this.load.image('Cybercar_head', asset('Cybercar_head'));
    this.load.image('Cybercar_body', asset('Cybercar_body'));
    this.load.image('Cybercar_tail', asset('Cybercar_tail'));

    // Powerups
    this.load.image('pu_vision', asset('vision'));
    this.load.image('pu_speed', asset('speed'));
    this.load.image('pu_multiplier', asset('multiplier'));
    // Il file della calamita si chiama `calamity.png`: e' il nome con cui e'
    // arrivato dal disegnatore, non un refuso da correggere qui — rinominarlo
    // significherebbe rinominare anche l'asset.
    this.load.image('pu_magnet', asset('calamity'));
    this.load.image('pu_radar', asset('radar'));
  }

  create() {
    // 1. Connessione al Server
    // Il token di sessione viaggia nell'handshake: il server ricava da li' il
    // wallet, una volta sola. Senza token la connessione viene rifiutata, quindi
    // non si puo' piu' giocare (ne' farmare missioni) per conto di un altro
    // indirizzo semplicemente scrivendolo nel payload.
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const initData = window.GAME_INITIAL_DATA || {};
    this.socket = io(API_BASE, {
      auth: { token: getToken(initData.wallet) },
    });

    // Connessione rifiutata (token assente o scaduto): non ha senso restare in
    // una partita che non partira' mai, si torna in lobby a rifare il login.
    this.socket.on('connect_error', (err) => {
      if (err.message === 'UNAUTHENTICATED') {
        window.dispatchEvent(new CustomEvent('return_to_landing'));
      }
    });

    // Ordine di profondità: Griglia(-100) -> Mine/Powerup/Cibo(-50) -> Giocatori(0)
    // Lancia la UIScene in parallelo per disegnare la HUD e Minimappa fissa senza zoom
    this.scene.launch('UIScene');

    // Le texture del cibo sono generate on-demand (una per colore) in foodTextureKey().

    this.foodGroup = this.add.group(); // Crea gruppo cibo
    // Indice id -> sprite. Il gruppo serve al disegno, questa mappa a trovare
    // una pallina in tempo costante quando ne arriva la differenza dal server.
    this.foodSprites = new Map();
    // Le sole palline che si stanno muovendo (risucchiate dal magnete): si
    // interpolano queste, non tutte e milleduecento.
    this.foodMoving = new Set();
    // Corpo dei serpenti, ricostruito dalle differenze: id -> [{x, y}, ...].
    // Il server manda il corpo intero solo quando un giocatore entra in vista;
    // poi dice soltanto "e' cresciuto" e quanti segmenti deve avere.
    this.bodies = new Map();
    this.powerupGroup = this.add.group(); // Gruppo per i powerups
    this.firewallGroup = this.add.group(); // Gruppo per i firewalls
    this.otherPlayers = this.add.group();
    this.myId = null;
    // Quanto l'orologio del server e' avanti rispetto a questo. Lo dice
    // `init_game`; finche' non arriva, si assume nessuno scarto.
    this.scartoOrologio = 0;

    // Prezzo del SOL, usato SOLO per affiancare una stima in dollari ai valori
    // di gioco. Non entra in nessun calcolo: l'economia e' in SOL dall'inizio
    // alla fine. Se non e' disponibile si mostrano solo i SOL, invece di
    // inventare un cambio fisso che sarebbe sbagliato per definizione.
    //
    // Viene dal modulo condiviso: la lobby lo ha gia' chiesto, e questa scena
    // riusa lo stesso valore invece di fare una seconda chiamata.
    this.solPrice = prezzoSolCorrente();
    getSolPrice()
      .then((v) => { this.solPrice = v; })
      .catch(() => { /* nessuna stima in dollari: il gioco funziona lo stesso */ });

    // --- AUDIO SYNTHESIZER ---
    this.synth = new Synthesizer(this);

    // Applica le impostazioni audio salvate (menu impostazioni) PRIMA di
    // avviare la musica, cosi' rispetta subito volume/mute scelti dall'utente.
    try {
      const saved = localStorage.getItem('vexel_audio_settings');
      if (saved) this.synth.applySettings(JSON.parse(saved));
    } catch (e) {}

    // Ascolta i cambi live dal menu impostazioni.
    this.handleAudioSettings = (e) => {
      if (this.synth && e.detail) this.synth.applySettings(e.detail);
    };
    window.addEventListener('vexel_audio_settings', this.handleAudioSettings);

    // Colonna sonora: drone ambientale scuro (parte al primo input utile,
    // per rispettare l'autoplay policy del browser).
    this.synth.startDrone();

    // 2. GESTIONE PULIZIA (Fondamentale per evitare i tuoi errori)
    // Quando questa scena si chiude (es. ricarichi pagina), stacchiamo i cavi
    this.events.on('shutdown', () => {
      if (this.socket) {
        this.socket.off('init_game');
        this.socket.off('game_state');
        this.socket.off('join_denied');
        this.socket.off('room_status');
        this.socket.off('connect_error');
        this.socket.disconnect();
      }

      // L'audio va staccato a mano: il contesto e' dell'app e sopravvive alla
      // partita, quindi il drone non si ferma piu' da solo quando la scena
      // muore (vedi game/audioContext.js).
      window.removeEventListener('vexel_audio_settings', this.handleAudioSettings);
      if (this.synth) {
        this.synth.destroy();
        this.synth = null;
      }
    });

    // 3. Join Game
    // Read initial data from React
    this.wallet = initData.wallet;
    this.tier = initData.tier;
    this.skin = initData.skin;
    this.ticketId = initData.ticketId;

    // Il tier non si dichiara piu': si presenta il ticket emesso da /api/entry e
    // il server legge da li' in che arena mettere il giocatore. Il wallet lo
    // ricava dal token dell'handshake, non da qui.
    this.socket.emit('join_game', { ticketId: this.ticketId, skin: this.skin });

    // Quanto mondo entra nello schermo di questo giocatore. Il server manda a
    // ciascuno solo i giocatori che puo' vedere (interest management), e senza
    // questa misura dovrebbe assumere una finestra da desktop per tutti: su un
    // telefono significherebbe mandare il triplo del necessario, su un 4K far
    // comparire i nemici dal nulla ai bordi.
    // Il server la restringe comunque entro limiti fissi: dichiararne una
    // enorme non fa vedere piu' lontano di un monitor grande vero.
    const inviaViewport = () => {
      if (!this.socket || !this.scale) return;
      // In pixel CSS, non fisici: al server serve sapere quanto MONDO entra
      // nello schermo di questo giocatore, e quello non cambia se disegniamo
      // gli stessi centimetri con il doppio dei pixel (vedi renderScale.js).
      this.socket.emit('viewport', {
        w: this.scale.width / RENDER_SCALE,
        h: this.scale.height / RENDER_SCALE,
      });
    };
    inviaViewport();
    this.scale.on('resize', inviaViewport);
    this.events.once('shutdown', () => this.scale && this.scale.off('resize', inviaViewport));

    // Ingresso rifiutato: ticket scaduto, gia' usato o mancante. E' un caso
    // normale (il giocatore ha lasciato la lobby aperta troppo a lungo), non un
    // errore: si torna in lobby, dove il ticket viene richiesto di nuovo.
    // La stanza puo' essere in attesa di altri giocatori: la scena non deve
    // saperne niente, si limita a inoltrare lo stato a React (che mostra la
    // sala d'attesa). Finche' si aspetta, il server non fa girare il loop.
    this.socket.on('room_status', (info) => {
      window.dispatchEvent(new CustomEvent('vexel_room_status', { detail: info }));
    });

    this.socket.on('join_denied', (info) => {
      console.warn(`Ingresso rifiutato: ${info?.code} — ${info?.message}`);
      window.dispatchEvent(new CustomEvent('return_to_landing'));
    });

    // Trigger visual feedback per la fee di ingresso
    let entryFee = 100;
    if (this.tier === 2) entryFee = 500;
    if (this.tier === 3) entryFee = 2000;

    // Usa setTimeout per essere sicuri che la UIScene sia pronta a ricevere l'evento
    setTimeout(() => {
      this.events.emit('action_burn', entryFee);
    }, 500);

    // 4. Listener: CONFIGURAZIONE INIZIALE
    // 4. Listener: CONFIGURAZIONE INIZIALE
    this.socket.on('init_game', (data) => {
      // Controllo di sicurezza
      if (!this.cameras || !this.cameras.main || !this.physics || !this.physics.world) {
        return;
      }

      this.myId = data.id;

      // Da qui in poi le scadenze dei power-up si leggono nell'ora del SERVER.
      // Vedi game/orologioServer.js: senza, un orologio locale avanti di cinque
      // secondi accorciava di cinque secondi ogni effetto.
      this.scartoOrologio = scartoOrologio(data.serverTime);

      // L'arena in cui si sta giocando DAVVERO. `this.tier` e' quella chiesta in
      // lobby, e il server non la guarda: il tier vero lo decide il ticket
      // (vedi GameEngine.addPlayer). Prendendo quello del client, chi entrasse
      // in una stanza diversa da quella richiesta vedrebbe il colore sbagliato.
      this.tierArena = data.tier || this.tier || 1;

      // Il quadro completo del cibo si riceve qui, una volta sola: da qui in
      // poi arrivano solo le differenze dentro game_state.
      this.resetFood(data.food || []);
      // Le mine sono immutabili per tutta la partita: disegnate qui e basta.
      this.updateFirewalls(data.firewalls);

      // Impostiamo i limiti del mondo e della telecamera
      this.cameras.main.setBounds(0, 0, data.mapSize, data.mapSize);
      // Si parte gia' alla scala giusta: lo zoom viene poi interpolato in
      // update(), e senza questa riga la partita comincerebbe con una zoomata
      // in avvicinamento di un paio di secondi.
      this.cameras.main.setZoom(RENDER_SCALE);
      this.physics.world.setBounds(0, 0, data.mapSize, data.mapSize);


      // Rimuoviamo vecchie griglie se ci sono (utile per i respawn)
      if (this.backgroundGrid) {
        this.backgroundGrid.destroy();
      }

      // Griglia esagonale a nido d'ape: un TileSprite che ripete una texture
      // generata a runtime. Disegnare i ~18.000 esagoni della mappa uno per uno
      // con Graphics affosserebbe il frame rate.
      this.backgroundGrid = this.add.tileSprite(
        data.mapSize / 2,  // X Centro
        data.mapSize / 2,  // Y Centro
        data.mapSize,      // Larghezza totale
        data.mapSize,      // Altezza totale
        this.hexTileTextureKey(this.tierArena)
      );

      // Mettiamo la griglia in profondità -1 per essere sicuri che stia SOTTO i giocatori
      this.backgroundGrid.setDepth(-100);

      // --- NUOVO: MASCHERA CIRCOLARE PER LA GRIGLIA ---
      const maskGraphics = this.make.graphics();
      maskGraphics.fillCircle(data.mapSize / 2, data.mapSize / 2, data.mapSize / 2);
      const mask = maskGraphics.createGeometryMask();
      this.backgroundGrid.setMask(mask);

      // Emblema VEXEL al centro esatto dell'arena: fa da punto di riferimento
      // per orientarsi sulla mappa, oltre che da firma.
      this.buildCenterEmblem(data.mapSize / 2, data.mapSize / 2, this.tierArena);

      this.mapSize = data.mapSize; // Salva la dimensione per la Red Zone
      if (this.redZoneGraphics) this.redZoneGraphics.destroy();
      this.redZoneGraphics = this.add.graphics();
      this.redZoneGraphics.setDepth(-90);
    });

    // 5. Listener: LOOP DI GIOCO
    this.socket.on('game_state', (serverData) => {
      // Se siamo morti, non aggiorniamo più nulla per lasciare l'effetto "freeze"
      if (this.isDead) return;

      // Chiamiamo la funzione di aggiornamento solo se la scena è viva
      if (this.sys && this.sys.isActive()) {
        // Il cibo arriva come differenza (nato / mosso / mangiato). La
        // fotografia intera torna solo ogni tanto, come risincronizzazione.
        // Coordinate per il RADAR: presenti solo mentre il power-up e' attivo.
        this.lastRadar = serverData.radar || null;

        if (serverData.foodFull) this.resetFood(serverData.foodFull);
        else if (serverData.foodDelta) this.applyFoodDelta(serverData.foodDelta);
        this.stepFoodMotion();

        this.updatePowerups(serverData.powerups);
        this.aggiornaFrenzy(serverData.powerups, serverData.frenzy);
        // I firewall non arrivano piu' a ogni tick: nascono con la stanza e non
        // cambiano mai, quindi si disegnano una volta sola da init_game.
        this.updatePlayers(serverData.players);

        // Update Red Zone
        if (serverData.redZoneRadius !== undefined && this.redZoneGraphics && this.mapSize) {
          this.redZoneGraphics.clear();
          const cx = this.mapSize / 2;
          const cy = this.mapSize / 2;
          // Outer glow
          this.redZoneGraphics.lineStyle(20, 0xFF0000, 0.2);
          this.redZoneGraphics.strokeCircle(cx, cy, serverData.redZoneRadius);
          // Inner solid line
          this.redZoneGraphics.lineStyle(5, 0xFF0000, 0.8);
          this.redZoneGraphics.strokeCircle(cx, cy, serverData.redZoneRadius);

          this.lastRedZoneRadius = serverData.redZoneRadius;
        }

        // Invia i dati a UIScene
        if (serverData.leaderboard) {
          this.events.emit('update_stats', serverData.leaderboard, this.myId);
        }
      }
    });

    // 6. Input Mouse & Boost
    this.isBoosting = false;
    this.isTouch = IS_TOUCH();

    // Boost col click, SOLO da mouse. Se il click parte su un elemento della UI
    // (header della leaderboard, pulsante cash out...) non deve far scattare il
    // boost: quei click comandano l'interfaccia.
    //
    // Su touch questo percorso non esiste proprio, e il perche' e' importante.
    // Phaser, con `inputWindowEvents` (acceso di serie), ascolta `touchstart`
    // anche su **window**: un dito appoggiato sul joystick — che e' un pannello
    // DOM sopra il canvas, non un oggetto di Phaser — porta comunque
    // `activePointer.isDown` a `true`. Il flag `pointerStartedOnUI` invece lo
    // muove solo l'evento di scena, e basta un `pointerup` di un altro dito per
    // azzerarlo: da li' in poi `isDown && !pointerStartedOnUI` restava vero per
    // tutto il tempo in cui il pollice sterzava. Risultato: **boost acceso
    // fisso** mentre si gioca — e, di rimbalzo, **cash out impossibile**, perche'
    // sul server il boost annulla l'incasso (`GameEngine.handleInput`).
    //
    // Phaser per giunta segue un solo dito (`input.activePointers` vale 1), e
    // questo gioco si tiene con due pollici: dedurre il boost da uno stato
    // globale del puntatore non poteva funzionare. Su touch il boost e' il
    // pulsante dedicato e basta.
    this.pointerStartedOnUI = false;
    this.input.on('pointerdown', (pointer) => {
      if (this.isTouch) return;
      const uiScene = this.scene.get('UIScene');
      const overUI = !!(uiScene && uiScene.input && uiScene.input.hitTestPointer(pointer).length > 0);
      this.pointerStartedOnUI = overUI;
      this.isBoosting = !overUI;
    });
    this.input.on('pointerup', () => {
      if (this.isTouch) return;
      this.isBoosting = false;
      this.pointerStartedOnUI = false;
    });

    // Particle Manager per il Boost (SAFE MODE)
    this.boostEmitter = null;
    try {
      const textureKey = 'flares';
      if (!this.textures.exists(textureKey)) {
        const graphics = this.make.graphics({ x: 0, y: 0, add: false });
        graphics.fillStyle(0xffffff, 1);
        graphics.fillCircle(4, 4, 4);
        graphics.generateTexture(textureKey, 8, 8);
      }

      // Tenta creazione particelle
      if (this.add.particles) {
        this.particles = this.add.particles(textureKey);
        if (this.particles && this.particles.createEmitter) {
          this.boostEmitter = this.particles.createEmitter({
            frame: 0,
            speed: 100,
            scale: { start: 1, end: 0 },
            blendMode: 'ADD',
            lifespan: 500,
            on: false
          });
          if (this.boostEmitter.setDepth) this.boostEmitter.setDepth(-10);
        }

        // Particle System per la Morte (STEP 4)
        if (this.particles && this.particles.createEmitter) {
          this.deathEmitter = this.particles.createEmitter({
            frame: 0,
            speed: { min: 50, max: 300 },
            angle: { min: 0, max: 360 },
            scale: { start: 1.5, end: 0 },
            blendMode: 'ADD',
            active: true,
            lifespan: 1000,
            gravityY: 0,
            on: false // Partirà solo su richiesta
          });
          this.deathEmitter.setDepth(50);
        }

      }
    } catch (err) {
      console.warn("⚠️ Particle System Failed:", err);
    }
    // Logic Input & Inertia
    this.input.on('pointermove', (pointer) => {
      if (!this.myPlayer || !this.socket || this.isDead) return;
      if (this.isTouch) return; // su mobile sterza solo il joystick

      // Se muove il mouse durante il cashout, annullalo
      if (this.myPlayer.isCashingOut) {
        this.socket.emit('cancel_cashout');
      }

      const targetAngle = Phaser.Math.Angle.Between(
        this.myPlayer.x,
        this.myPlayer.y,
        pointer.worldX,
        pointer.worldY
      );

      // --- INERZIA / TURN RATE DELEGATA AL SERVER ---
      // Invia l'angolo bersaglio grezzo, il server calcolerà la curva fluida reale
      this.currentRotation = targetAngle;
    });

    this.handleJoystickMove = (e) => {
      if (this.isDead) return;
      // Stesso patto del mouse: se ti muovi mentre stai incassando, annulli.
      if (this.socket && this.myPlayer && this.myPlayer.isCashingOut) {
        this.socket.emit('cancel_cashout');
      }
      this.currentRotation = e.detail.angle;
    };

    this.handleJoystickBoost = (e) => {
      if (this.isDead) return;
      this.isJoystickBoosting = e.detail.active;
    };

    window.addEventListener('joystick_move', this.handleJoystickMove);
    window.addEventListener('joystick_boost', this.handleJoystickBoost);

    // Listener per Cash Out via tastiera
    this.input.keyboard.on('keydown-C', () => {
      if (!this.isDead && this.socket && this.myPlayer && !this.myPlayer.isCashingOut) {
        this.socket.emit('start_cashout');
      }
    });
    // Listener per attivare il PowerUp in tasca via tastiera (tasto E)
    this.input.keyboard.on('keydown-E', () => {
      if (!this.isDead && this.socket && this.myPlayer && this.myPlayer.heldPowerup) {
        this.socket.emit('activate_powerup');
      }
    });
    // Apri/chiudi la leaderboard da tastiera (tasto L). Il toggle vive nella UIScene.
    this.input.keyboard.on('keydown-L', () => {
      this.game.events.emit('request_lb_toggle');
    });
    // Supporto per bottone personalizzato custom_powerup (UI esterna / mobile)
    this.handleCustomPowerup = () => {
      if (!this.isDead && this.socket && this.myPlayer && this.myPlayer.heldPowerup) {
        this.socket.emit('activate_powerup');
      }
    };
    window.addEventListener('custom_powerup', this.handleCustomPowerup);

    // Supporto per bottone personalizzato custom_cashout
    this.handleCustomCashout = () => {
      if (!this.isDead && this.socket && this.myPlayer && !this.myPlayer.isCashingOut) {
        this.socket.emit('start_cashout');
      }
    };
    window.addEventListener('custom_cashout', this.handleCustomCashout);

    this.events.on('shutdown', () => {
      window.removeEventListener('joystick_move', this.handleJoystickMove);
      window.removeEventListener('joystick_boost', this.handleJoystickBoost);
      window.removeEventListener('custom_cashout', this.handleCustomCashout);
      window.removeEventListener('custom_powerup', this.handleCustomPowerup);
      window.removeEventListener('vexel_audio_settings', this.handleAudioSettings);
    });

    // 7. Listener: DEATH / GAME OVER
    // ...
    const handleDeathEvent = (data) => {
      if (this.isDead) return; // Già morto
      this.handleDeath(data);
    };

    this.socket.on('death', handleDeathEvent);
    this.socket.on('game_over', handleDeathEvent);
    this.socket.on('player_death', handleDeathEvent);

    this.socket.on('kill_feed', (data) => {
      this.events.emit('kill_feed_event', data);
    });

    // DEBUG: Premi 'D' per simulare la morte
    this.input.keyboard.on('keydown-D', () => {
      console.log("Simulating Death...");
      handleDeathEvent({ score: Math.floor(Math.random() * 1000) });
    });

    // SPACEBAR for Boost
    this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // I pulsanti della schermata di fine partita non si agganciano piu' da qui:
    // sono di GameOverPopup, che li gestisce come un normale componente React.

    // Countdown power-up e "tasca" (bag) vivono nella UIScene: quella scena non
    // subisce lo zoom del Vision, quindi restano ancorati al bordo dello schermo.
    // Qui ci limitiamo a calcolarne i valori in update() e a inviarli via evento.
  }

  update(time, delta) {
    if (!this.myPlayer || !this.socket || this.isDead) return;

    this.pulseFood(time);

    // Gestione Boost (barra spaziatrice, click, o pulsante del joystick).
    // La regola sta in game/boostInput.js, con il perche' e le sue prove.
    this.isBoosting = boostAttivo({
      spazio: !!(this.spaceKey && this.spaceKey.isDown),
      puntatoreGiu: this.input.activePointer.isDown,
      partitoDaUI: this.pointerStartedOnUI,
      touch: this.isTouch,
      joystick: this.isJoystickBoosting,
    });

    // Invia Input al Server (ogni frame o throttled)
    // Usiamo l'ultimo angolo calcolato dal mouse/joystick
    if (this.currentRotation !== undefined) {
      this.socket.emit('input_update', {
        angle: this.currentRotation,
        isBoosting: this.isBoosting
      });
    }

    // Aggiorna Emettitore Particelle
    if (this.isBoosting && this.boostEmitter) {
      // Trova la coda
      const tail = this.myPlayer.bodySegments && this.myPlayer.bodySegments.length > 0
        ? this.myPlayer.bodySegments[this.myPlayer.bodySegments.length - 1]
        : this.myPlayer; // Fallback sulla testa se non ha corpo

      // Posiziona l'emettitore sulla coda
      this.boostEmitter.setPosition(tail.x, tail.y);

      // Emetti particelle (manuale o automatico)
      // Se usiamo 'on: false' nell'emitter, dobbiamo chiamare emitParticle o start/stop
      this.boostEmitter.emitParticle(2);
    }

    // --- INVIA DATI A UISCENE PER MINIMAP (STEP 4) ---
    if (this.myPlayer && this.backgroundGrid && this.mapSize) {
      // Calcola proporzione 0-1 rispetto alla mappa intera
      const pctX = this.myPlayer.x / this.mapSize;
      const pctY = this.myPlayer.y / this.mapSize;

      // Invia i dati alla UIScene senza muovere oggetti grafici qui
      this.events.emit('update_minimap', pctX, pctY, this.lastRedZoneRadius, this.mapSize);
    }

    // --- HUD POWERUP (countdown + bag) -> UIScene ---
    // Calcoliamo qui i valori e li spediamo alla UIScene, che li disegna ancorati
    // al bordo (immune allo zoom del Vision).
    if (this.myPlayer) {
      const now = Date.now();

      // Countdown dell'effetto attivo piu' lungo.
      let timer = null;
      const effects = this.myPlayer.lastEffects;
      if (effects) {
        // type -> campo con la scadenza in activeEffects (lato server).
        const timed = {
          VISION: 'visionEndTime',
          SPEED: 'speedEndTime',
          MULTIPLIER: 'multiplierEndTime',
          MAGNET: 'magnetEndTime',
          RADAR: 'radarEndTime',
        };

        let maxTimeLeft = 0;
        let activeType = null;

        for (const type in timed) {
          const end = effects[timed[type]];
          const left = tempoRestante(end, this.scartoOrologio, now);
          if (left > maxTimeLeft) { maxTimeLeft = left; activeType = type; }
        }

        if (maxTimeLeft > 0) {
          const style = powerupStyle(activeType);
          timer = {
            text: `${style.icon} ${style.label}: ${(maxTimeLeft / 1000).toFixed(1)}s`,
            color: hexColor(style.color)
          };
        }
      }

      // Power-up in tasca (attivabile col tasto E).
      let held = null;
      if (this.myPlayer.heldPowerup) {
        const style = powerupStyle(this.myPlayer.heldPowerup);
        held = { name: style.label, color: hexColor(style.color), icon: style.icon };
      }

      this.events.emit('update_powerup_hud', { timer, held });

      // --- RADAR: posizioni dei nemici verso la minimappa ---
      // Il pallino nemico e' un vantaggio a tempo: fuori dall'effetto la
      // UIScene non deve nemmeno ricevere le coordinate.
      // Le coordinate dei nemici arrivano ora in un campo a parte (`radar`),
      // mandato dal server SOLO mentre il power-up e' attivo. Prima si
      // ricavavano da `lastPlayersData`, che conteneva tutti i giocatori: con
      // l'interest management quel campo contiene solo chi e' in vista, cioe'
      // proprio quelli che il radar non serve a trovare.
      const radarOn = !!effects && ancoraAttivo(effects.radarEndTime, this.scartoOrologio, now);
      if (radarOn && this.mapSize && this.lastRadar) {
        const blips = this.lastRadar.map(([x, y]) => ({ x: x / this.mapSize, y: y / this.mapSize }));
        this.events.emit('update_radar', blips);
      } else if (this.radarWasOn) {
        this.events.emit('update_radar', null);
      }
      this.radarWasOn = radarOn;
    }
  }

  handleDeath(data) {
    console.log("🔥 [MainScene] Game Over Triggered!", data);

    // STOP EVERYTHING
    this.isDead = true;

    if (this.synth) {
      this.synth.stopDrone();
      this.synth.stopBoost();
      this.synth.playDeath();
    }

    // Opzionale: Ferma la fisica o animazioni
    if (this.physics && this.physics.world) {
      this.physics.pause();
    }

    // STEP 4: Effetto luminoso di esplosione
    if (this.deathEmitter && this.myPlayer) {
      this.deathEmitter.setPosition(this.myPlayer.x, this.myPlayer.y);
      this.deathEmitter.explode(100); // Esplode in 100 particelle contemporaneamente

      // Fai scomparire il corpo del giocatore per evitare che rimanga "congelato" sulla mappa
      this.myPlayer.setVisible(false);
      if (this.myPlayer.bodySegments) this.myPlayer.bodySegments.forEach(s => s.setVisible(false));
      if (this.myPlayer.leftEye) this.myPlayer.leftEye.setVisible(false);
      if (this.myPlayer.rightEye) this.myPlayer.rightEye.setVisible(false);
      if (this.myPlayer.leftPupil) this.myPlayer.leftPupil.setVisible(false);
      if (this.myPlayer.rightPupil) this.myPlayer.rightPupil.setVisible(false);
      if (this.myPlayer.nameText) this.myPlayer.nameText.setVisible(false);
      if (this.myPlayer.scoreText) this.myPlayer.scoreText.setVisible(false);
      if (this.myPlayer.logoText) this.myPlayer.logoText.setVisible(false);
    }

    // 1. Calcola Unrealized Gains o Profitti Reali
    let lossAmount = this.calculatePotentialWin(data.score || 0);
    const reason = data.reason || "UNKNOWN";
    let finalPayoutStr = data.finalPayout !== undefined ? this.calculatePotentialWin(data.finalPayout) : lossAmount;
    // La tassa e' in SOL come tutto il resto: prima era stampata con un `$`
    // davanti a un numero che ormai non erano dollari.
    let taxPaidStr = this.calculatePotentialWin(data.taxPaid || 0);

    // Lo sblocco della BinarySnake (e ogni altra missione) ora lo decide il server
    // sui dati reali della partita e assegna la skin al wallet: il client non se la
    // sblocca piu' da solo (era falsificabile). Al ritorno in lobby, LandingPage
    // ricarica il profilo dal server e la skin appena vinta compare.

    // 2. Mostra Popup. `credited` dice quanto e' finito davvero sul saldo: in
    // fase di test l'ingresso e' gratuito e quindi non viene accreditato nulla,
    // e il pannello deve dirlo invece di far credere a una vincita incassata.
    this.showFomoPopup(lossAmount, reason, data.survivalTime, finalPayoutStr, taxPaidStr, data.taxPaid || 0, data.credited);
  }

  // Il punteggio arriva dal server **in SOL**: e' quella la valuta del gioco.
  // I dollari sono solo una traduzione per l'occhio, calcolata col prezzo del
  // momento. Prima era il contrario (punteggio in dollari, SOL stimati), e
  // bastava che il prezzo si muovesse perche' i due numeri non tornassero.
  //
  // Se il prezzo non e' disponibile si mostrano solo i SOL: meglio un'etichetta
  // in meno che una cifra in dollari inventata.
  calculatePotentialWin(scoreSol) {
    const sol = Number(scoreSol) || 0;
    const solana = sol.toFixed(4);
    if (!this.solPrice) return `${solana} SOL`;
    const dollars = (sol * this.solPrice).toFixed(2);
    return `${solana} SOL (~$${dollars})`;
  }

  // La scena non disegna piu' la schermata di fine partita: si limita a dire
  // com'e' finita. A mostrarla ci pensa il componente React GameOverPopup, che
  // ascolta questo evento. Prima qui c'erano ottanta righe di `getElementById`,
  // `innerHTML` e stili scritti a mano su nodi definiti in `public/index.html`:
  // qualunque ritocco a quel markup rompeva il gioco senza dire niente.
  showFomoPopup(lossAmount, reason, survivalTime = 0, finalPayoutStr = "", taxPaidStr = "", rawTax = 0, credited) {
    window.dispatchEvent(new CustomEvent('vexel_game_over', {
      detail: {
        reason,
        loss: lossAmount,
        finalPayout: finalPayoutStr,
        taxPaid: taxPaidStr,
        rawTax,
        survivalTime,
        credited,
      },
    }));
  }

  updateFirewalls(firewallsData) {
    if (this.isDead || !firewallsData || !this.firewallGroup) return;

    // 1. Rimuovi firewall che non esistono più (purged dalla safe zone)
    this.firewallGroup.getChildren().forEach((fwSprite) => {
      if (!firewallsData[fwSprite.id]) {
        fwSprite.destroy();
      }
    });

    // 2. Aggiungi nuovi firewall (reset safe zone)
    const fwIds = Object.keys(firewallsData);
    for (let i = 0; i < fwIds.length; i++) {
      const id = fwIds[i];
      const fw = firewallsData[id];

      let fwSprite = this.firewallGroup.getChildren().find(sprite => sprite.id === id);

      if (!fwSprite) {
        fwSprite = this.add.circle(fw.x, fw.y, fw.radius, 0x1a001a);
        fwSprite.setStrokeStyle(4, 0xff0055);
        fwSprite.setDepth(-80);
        fwSprite.id = id;

        this.tweens.add({
          targets: fwSprite,
          alpha: 0.6,
          scale: 1.05,
          duration: 800 + Math.random() * 400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });

        this.firewallGroup.add(fwSprite);
      }
    }
  }

  // Texture del cibo per un dato colore: nucleo pieno colorato + alone morbido dello
  // stesso colore, con un piccolo cuore chiaro al centro per il "brillio". Il colore
  // e' COTTO nel gradiente (niente setTint), quindi si vede sia in WebGL che in Canvas.
  // Cachata per colore: la palette del cibo e' piccola, quindi restano poche texture.
  foodTextureKey(color) {
    const c = (typeof color === 'number') ? color : 0xFFFFFF;
    const key = FOOD_TEX_PREFIX + c;
    if (this.textures.exists(key)) return key;

    const size = 64;
    const half = size / 2;
    const r = (c >> 16) & 0xFF;
    const g = (c >> 8) & 0xFF;
    const b = c & 0xFF;
    const rgb = `${r},${g},${b}`;

    const tex = this.textures.createCanvas(key, size, size);
    const ctx = tex.getContext();
    const grd = ctx.createRadialGradient(half, half, 0, half, half, half);
    grd.addColorStop(0, 'rgba(255,255,255,0.9)');   // cuore chiaro (brillio)
    grd.addColorStop(0.18, `rgba(${rgb},1)`);       // colore pieno
    grd.addColorStop(0.5, `rgba(${rgb},1)`);
    grd.addColorStop(0.64, `rgba(${rgb},0.5)`);     // alone
    grd.addColorStop(0.85, `rgba(${rgb},0.16)`);
    grd.addColorStop(1, `rgba(${rgb},0)`);
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
    return key;
  }

  // --- GRIGLIA ESAGONALE ---
  // Texture ripetibile con un nido d'ape di esagoni "flat-top". Il reticolo si
  // ripete ogni 3R in orizzontale e ogni R*√3 in verticale: scegliendo 4 e 7
  // periodi dentro un tile quadrato i due passi tornano quasi esatti, e gli
  // esagoni tagliati dai bordi ricombaciano con quelli del tile adiacente.
  // La texture e' una per arena, e la chiave se lo porta dietro: con una chiave
  // sola la prima griglia disegnata sarebbe rimasta in cache e tutte le arene
  // successive avrebbero riusato il suo colore.
  hexTileTextureKey(tier) {
    const colori = coloriArena(tier);
    const key = `hex_grid_tile_t${tier}`;
    if (this.textures.exists(key)) return key;

    const periodX = HEX_TILE / HEX_PERIODS_X;  // = 3R
    const periodY = HEX_TILE / HEX_PERIODS_Y;  // = R√3
    const R = periodX / 3;                     // raggio orizzontale
    const Ry = periodY / Math.sqrt(3);         // raggio verticale

    const tex = this.textures.createCanvas(key, HEX_TILE, HEX_TILE);
    const ctx = tex.getContext();

    ctx.fillStyle = colori.fondo; // quasi nero, col soffio di colore dell'arena
    ctx.fillRect(0, 0, HEX_TILE, HEX_TILE);

    ctx.strokeStyle = colori.griglia; // il colore dell'arena, appena accennato
    ctx.lineWidth = 1;

    // Colonne distanziate di 1,5R, quelle dispari sfalsate di mezzo passo:
    // e' il classico incastro a nido d'ape.
    for (let i = -1; i <= HEX_PERIODS_X * 2 + 1; i++) {
      const cx = i * (periodX / 2);
      const offsetY = (i % 2 === 0) ? 0 : periodY / 2;

      for (let j = -1; j <= HEX_PERIODS_Y + 1; j++) {
        const cy = j * periodY + offsetY;

        ctx.beginPath();
        for (let k = 0; k < 6; k++) {
          const a = (Math.PI / 3) * k;
          const x = cx + Math.cos(a) * R;
          const y = cy + Math.sin(a) * Ry;
          if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }

    tex.refresh();
    return key;
  }

  // Emblema VEXEL al centro dell'arena: esagoni concentrici al neon con il
  // wordmark. Puro decoro, quindi sta sotto a tutto il resto e non intercetta input.
  buildCenterEmblem(cx, cy, tier) {
    if (this.centerEmblem) this.centerEmblem.destroy();

    const container = this.add.container(cx, cy).setDepth(-99);

    // L'emblema porta il colore della stanza, come la griglia: entrando si
    // capisce dove si e' senza leggere niente. Le trasparenze passano da
    // `alfaArena`, che le corregge col peso dell'arena — senza, in HIGH COMMAND
    // l'emblema rosso sarebbe risultato molto piu' spento degli altri due.
    const colori = coloriArena(tier);
    const TINTA = colori.accentoInt;
    const alfa = (a) => alfaArena(colori, a);

    const hexPoints = (r, rot = 0) => {
      const pts = [];
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k + rot;
        pts.push(new Phaser.Math.Vector2(Math.cos(a) * r, Math.sin(a) * r));
      }
      return pts;
    };

    const g = this.add.graphics();

    // Alone diffuso: si intravede da lontano e dice "qui e' il centro".
    g.fillStyle(TINTA, alfa(0.03));
    g.fillPoints(hexPoints(560), true);

    // Anelli esagonali concentrici, sempre piu' tenui verso l'esterno.
    [[520, 0.1], [400, 0.16], [300, 0.26]].forEach(([r, alpha]) => {
      g.lineStyle(4, TINTA, alfa(alpha));
      g.strokePoints(hexPoints(r), true, true);
    });

    // Esagono interno pieno, il "core".
    g.fillStyle(colori.coreInt, 0.55);
    g.fillPoints(hexPoints(230), true);
    g.lineStyle(6, TINTA, alfa(0.5));
    g.strokePoints(hexPoints(230), true, true);

    // Raggi dai vertici dell'esagono interno verso l'anello successivo.
    g.lineStyle(2, TINTA, alfa(0.18));
    hexPoints(1).forEach((v) => g.lineBetween(v.x * 240, v.y * 240, v.x * 296, v.y * 296));

    container.add(g);

    const wordmark = this.add.text(0, 0, 'VEXEL', {
      fontFamily: '"Space Mono", monospace',
      fontSize: '96px',
      color: colori.accento,
      fontStyle: 'bold'
    }).setOrigin(0.5).setAlpha(alfa(0.75));
    wordmark.setLetterSpacing(18);
    wordmark.setShadow(0, 0, colori.accento, 30);

    const subtitle = this.add.text(0, 72, 'ARENA · CORE', {
      fontFamily: '"Space Mono", monospace',
      fontSize: '26px',
      color: colori.accento
    }).setOrigin(0.5).setAlpha(alfa(0.35));
    subtitle.setLetterSpacing(10);

    container.add([wordmark, subtitle]);

    // Respiro lentissimo: l'emblema "vive" senza distrarre durante il gioco.
    this.tweens.add({
      targets: container,
      alpha: { from: 0.85, to: 1 },
      scale: { from: 0.985, to: 1.015 },
      duration: 3200, yoyo: true, repeat: -1, ease: 'Sine.easeInOut'
    });

    this.centerEmblem = container;
  }

  // Alone morbido per i power-up: stesso trucco del cibo (colore cotto nella
  // texture, niente setTint) cosi' funziona anche col renderer Canvas.
  // Lo stato della frenesia arriva a ogni tick e va girato alla UIScene, che
  // disegna countdown e faro. Qui si tiene anche `frenzyPortatoreId`, che serve
  // a marcare il portatore fra i giocatori disegnati.
  aggiornaFrenzy(powerups, frenzy) {
    const attiva = !!frenzy;
    this.frenzyPortatoreId = attiva ? frenzy.portatoreId : null;

    if (attiva !== this.frenzyEraAttiva) {
      this.frenzyEraAttiva = attiva;
      if (attiva && this.synth) this.synth.playPowerup();
    }
    this.events.emit('update_frenzy', frenzy || null);

    // Il faro: dove sta il nucleo, in coordinate normalizzate sulla mappa.
    // Il server lo manda a tutti anche fuori vista proprio per questo.
    let faro = null;
    if (powerups && this.mapSize) {
      for (const id in powerups) {
        if (powerups[id].type === 'FRENZY') {
          faro = { x: powerups[id].x / this.mapSize, y: powerups[id].y / this.mapSize };
          break;
        }
      }
    }
    if (faro || this.faroFrenzyAcceso) {
      this.events.emit('update_frenzy_beacon', faro);
      this.faroFrenzyAcceso = !!faro;
    }
  }

  auraTextureKey(color) {
    const c = color || 0xFFFFFF;
    const key = AURA_TEX_PREFIX + c;
    if (this.textures.exists(key)) return key;

    const size = 128;
    const half = size / 2;
    const rgb = `${(c >> 16) & 0xFF},${(c >> 8) & 0xFF},${c & 0xFF}`;

    const tex = this.textures.createCanvas(key, size, size);
    const ctx = tex.getContext();
    const grd = ctx.createRadialGradient(half, half, 0, half, half, half);
    grd.addColorStop(0, `rgba(${rgb},0.55)`);   // cuore, sotto l'icona
    grd.addColorStop(0.3, `rgba(${rgb},0.28)`);
    grd.addColorStop(0.6, `rgba(${rgb},0.1)`);
    grd.addColorStop(1, `rgba(${rgb},0)`);      // sfuma nel nero della griglia
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, size, size);
    tex.refresh();
    return key;
  }

  // Respiro del cibo base. Un tween per particella sarebbe piu' comodo, ma con
  // oltre mille particelle in scena il tween manager ne risentirebbe: qui basta
  // un seno per sprite, calcolato una volta per frame.
  pulseFood(time) {
    if (!this.foodGroup) return;

    const children = this.foodGroup.getChildren();
    for (let i = 0; i < children.length; i++) {
      const sprite = children[i];
      if (sprite.pulseScale === undefined) continue; // i drop hanno il loro tween
      const wave = Math.sin(time * sprite.pulseOmega + sprite.pulsePhase);
      const scale = sprite.pulseScale * (1 + FOOD_PULSE_AMPLITUDE * wave);
      sprite.scaleX = scale;
      sprite.scaleY = scale;
    }
  }

  // --- CIBO: PROTOCOLLO A DIFFERENZE ------------------------------------------
  //
  // Prima il server rimandava tutte le palline a ogni tick e questa funzione
  // ricostruiva la scena confrontando fotografia e sprite: 1200 confronti
  // trenta volte al secondo, per scoprire quasi sempre che non era cambiato
  // niente. Erano anche 170 kB a tick sulla rete, cioe' il 73% del traffico.
  //
  // Ora arrivano tre liste — nate, mosse, mangiate — e qui si applicano. Le
  // palline ferme non vengono nemmeno nominate.

  // Crea lo sprite di una pallina. `f` e' nel formato compatto del server:
  // { i: id, x, y, c: colore, d: 1 se e' un resto di giocatore, v: valore }.
  createFoodSprite(f) {
    const id = f.i;
    let sprite;

    if (f.d) {
      // Resti dei giocatori: il cerchio pulsante col bordo bianco.
      sprite = this.add.circle(f.x, f.y, 12, f.c);
      sprite.setStrokeStyle(3, 0xFFFFFF, 0.8);
      this.tweens.add({
        targets: sprite,
        scale: 1.3,
        alpha: 0.6,
        duration: 500,
        yoyo: true,
        repeat: -1
      });
    } else {
      // Cibo base: una sola Image con la texture gia' cotta nel colore, cosi'
      // resta batchata e si vede anche col renderer Canvas.
      sprite = this.add.image(f.x, f.y, this.foodTextureKey(f.c));

      // Misura, fase e ritmo del respiro derivati dall'id: due particelle
      // vicine non pulsano mai in sincrono.
      const r = hash01(id);
      const size = FOOD_DRAW_MIN + r * (FOOD_DRAW_MAX - FOOD_DRAW_MIN);
      sprite.setDisplaySize(size, size);
      sprite.pulseScale = sprite.scaleX;
      sprite.pulsePhase = r * Math.PI * 2;
      // hash01 sull'id girato: serve un secondo numero scorrelato dal primo, o
      // le particelle grandi pulserebbero tutte piu' lente delle piccole.
      const r2 = hash01(id.split('').reverse().join(''));
      sprite.pulseOmega = (Math.PI * 2) / (FOOD_PULSE_MS_MIN + r2 * (FOOD_PULSE_MS_MAX - FOOD_PULSE_MS_MIN));
    }

    sprite.id = id;
    // Dove lo sta portando il magnete. Di norma coincide con la posizione.
    sprite.targetX = f.x;
    sprite.targetY = f.y;
    sprite.setDepth(-50); // Sotto i giocatori
    this.foodGroup.add(sprite);
    this.foodSprites.set(id, sprite);
    return sprite;
  }

  destroyFoodSprite(id) {
    const sprite = this.foodSprites.get(id);
    if (!sprite) return;
    this.foodSprites.delete(id);
    this.foodMoving.delete(id);
    sprite.destroy();
  }

  // Quadro completo: da init_game (ingresso in partita) e dalla
  // risincronizzazione periodica del server.
  resetFood(lista) {
    if (!this.foodGroup) return;

    const visti = new Set();
    for (const f of lista) {
      visti.add(f.i);
      const sprite = this.foodSprites.get(f.i);
      if (!sprite) {
        this.createFoodSprite(f);
      } else {
        // Gia' presente: si riallinea la posizione senza ricrearlo, altrimenti
        // ogni risincronizzazione farebbe ripartire i tween e il respiro.
        sprite.targetX = f.x;
        sprite.targetY = f.y;
        this.foodMoving.add(f.i);
      }
    }

    // Quello che il server non ha nominato non esiste piu'. E' anche il punto
    // in cui una pallina fantasma — sopravvissuta a un difetto nei delta —
    // sparisce da sola.
    for (const id of [...this.foodSprites.keys()]) {
      if (!visti.has(id)) this.destroyFoodSprite(id);
    }
  }

  applyFoodDelta(delta) {
    if (!this.foodGroup || !delta) return;

    // L'ordine conta: prima nascono, poi si muovono, poi spariscono.
    if (delta.add) {
      for (const f of delta.add) {
        if (this.foodSprites.has(f.i)) this.destroyFoodSprite(f.i);
        this.createFoodSprite(f);
      }
    }

    if (delta.move) {
      for (const [id, x, y] of delta.move) {
        const sprite = this.foodSprites.get(id);
        if (!sprite) continue;
        sprite.targetX = x;
        sprite.targetY = y;
        this.foodMoving.add(id);
      }
    }

    if (delta.remove) {
      for (const id of delta.remove) this.destroyFoodSprite(id);
    }
  }

  // Avvicina alla meta le sole palline che il magnete sta risucchiando.
  //
  // Serve perche' il server annuncia una posizione nuova solo nei tick in cui
  // la pallina si muove davvero: senza questo passo, l'ultima frazione di
  // movimento resterebbe a meta' strada per sempre.
  stepFoodMotion() {
    if (this.foodMoving.size === 0) return;

    for (const id of [...this.foodMoving]) {
      const sprite = this.foodSprites.get(id);
      if (!sprite) { this.foodMoving.delete(id); continue; }

      const dx = sprite.targetX - sprite.x;
      const dy = sprite.targetY - sprite.y;

      // Arrivata: si toglie dall'insieme, cosi' l'interpolazione costa in
      // proporzione a quante palline si stanno muovendo davvero (poche) e non
      // a quante ce ne sono in mappa (moltissime).
      if (Math.abs(dx) < 0.2 && Math.abs(dy) < 0.2) {
        sprite.x = sprite.targetX;
        sprite.y = sprite.targetY;
        this.foodMoving.delete(id);
        continue;
      }

      // Interpolazione: il risucchio non deve scattare al ritmo dei tick.
      sprite.x += dx * 0.35;
      sprite.y += dy * 0.35;
    }
  }

  updatePowerups(powerupsData) {
    if (this.isDead || !this.powerupGroup || !powerupsData) return;

    const puIds = Object.keys(powerupsData);

    // 1. Rimuovi powerup raccolti
    this.powerupGroup.getChildren().forEach((puSprite) => {
      if (!powerupsData[puSprite.id]) {
        puSprite.destroy();
      }
    });

    // 2. Aggiungi/Aggiorna powerup
    puIds.forEach((id) => {
      const pu = powerupsData[id];
      let puSprite = this.powerupGroup.getChildren().find(sprite => sprite.id === id);

      if (!puSprite) {
        const style = POWERUP_STYLES[pu.type] || { color: 0xFFFFFF, icon: '?' };

        // Creiamo un contenitore per l'elemento
        puSprite = this.add.container(pu.x, pu.y);
        puSprite.id = id;
        puSprite.setDepth(-45);

        // Alone luminoso del colore del power-up: si vede da lontano e
        // distingue i tipi a colpo d'occhio anche prima di leggere l'icona.
        const aura = this.add.image(0, 0, this.auraTextureKey(style.color));
        // Il nucleo della frenesia si deve vedere da lontano: alone doppio.
        const scalaAura = pu.type === 'FRENZY' ? 12 : 7;
        aura.setDisplaySize(pu.radius * scalaAura, pu.radius * scalaAura);
        aura.setBlendMode(Phaser.BlendModes.ADD);
        aura.setAlpha(0.75);
        puSprite.add(aura);

        let targetToTween = null;
        let initialScale = 1;

        if (style.imageKey) {
          const img = this.add.image(0, 0, style.imageKey);
          // Adattiamo l'immagine per essere un po' più grande del raggio base
          initialScale = (pu.radius * 3) / (img.width || 100);
          img.setScale(initialScale);
          puSprite.add(img);
          targetToTween = img;
        } else {
          const circle = this.add.circle(0, 0, pu.radius, style.color);
          circle.setStrokeStyle(2, 0xFFFFFF); // Bordino bianco per risaltare
          const icon = this.add.text(0, 0, style.icon, {
            fontSize: (pu.radius * 1.2) + 'px',
            fontFamily: 'Arial'
          }).setOrigin(0.5);
          puSprite.add([circle, icon]);
          targetToTween = circle;
        }

        // Fai pulsare leggermente i powerup
        this.tweens.add({
          targets: targetToTween,
          scale: initialScale * 1.2,
          duration: 800,
          yoyo: true,
          repeat: -1
        });

        // L'alone respira in controfase e piu' ampio: da' l'effetto "pulsar".
        this.tweens.add({
          targets: aura,
          scale: { from: aura.scale * 0.82, to: aura.scale * 1.18 },
          alpha: { from: 0.45, to: 0.9 },
          duration: 1100,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut'
        });

        // Il nucleo gira su se' stesso: e' l'unico power-up che lo fa, e basta
        // quello per riconoscerlo con la coda dell'occhio.
        if (pu.type === 'FRENZY') {
          this.tweens.add({
            targets: puSprite, angle: 360, duration: 4000, repeat: -1, ease: 'Linear',
          });
        }

        this.powerupGroup.add(puSprite);
      }
    });
  }

  // Ricostruisce il corpo di un serpente dalle differenze mandate dal server.
  //
  // Il corpo e' la scia delle posizioni della testa, un punto per tick. Il
  // server pero' NON lo allunga sempre (giocatore morto, cash-out, uscita dai
  // bordi), quindi qui non si indovina niente: si aggiunge un punto solo se il
  // server dice `g`, e si taglia alla lunghezza `n` che dichiara lui.
  //
  // Sbagliare questo conto non produce collisioni sbagliate — quelle le calcola
  // il server sul corpo vero — ma disegnerebbe un corpo dove non e', che per chi
  // gioca e' anche peggio: si muore contro il nulla.
  rebuildBody(id, pData) {
    let trail = this.bodies.get(id);

    if (pData.h) {
      // Corpo intero: prima comparsa in vista, o rientro dopo essere uscito.
      trail = pData.h.map(([x, y]) => ({ x, y }));
    } else {
      if (!trail) trail = [];
      if (pData.g) trail.unshift({ x: pData.x, y: pData.y });
    }

    // La lunghezza la decide il server: qui non si replica la formula della
    // massa, che e' il tipo di duplicato che prima o poi diverge.
    if (typeof pData.n === 'number' && trail.length > pData.n) trail.length = pData.n;

    this.bodies.set(id, trail);
    return trail;
  }

  updatePlayers(playersData) {
    if (this.isDead) return; // Stop updates

    // RIMOSSO: Controllo implicito di morte.
    // Causava falsi positivi ("morti fantasma senza collisione") se il server subiva un rallentamento,
    // uno split di pacchetti Socket.io, o un riavvio a caldo (nodemon) durante lo sviluppo.
    // Ora la morte è dettata esclusivamente dall'evento 'game_over' inviato esplicitamente dal server.

    if (!this.add || !this.sys || !this.otherPlayers || !this.otherPlayers.active) return;

    // Ultimo stato grezzo dal server: al Radar servono le coordinate di tutti,
    // anche di chi e' fuori dallo schermo e quindi non ha uno sprite vivo.
    this.lastPlayersData = playersData;

    Object.keys(playersData).forEach((id) => {
      const pData = playersData[id];
      // Il corpo non arriva piu' a ogni tick: si ricostruisce qui e si rimette
      // dove il resto del disegno se lo aspetta.
      pData.history = this.rebuildBody(id, pData);
      let playerRef;

      // Recupera o crea il riferimento al giocatore
      if (id === this.myId) {
        playerRef = this.myPlayer;
      } else {
        const children = this.otherPlayers.getChildren();
        playerRef = children.find(p => p.playerId === id);
      }

      // --- FASE 1: CREAZIONE INIZIALE ---
      if (!playerRef) {
        const r = pData.radius || 20; // Radius di base
        const headColor = pData.color;
        let head;

        const isImageSkin = IMAGE_SKINS.includes(pData.skin);

        if (isImageSkin) {
          head = this.add.sprite(pData.x, pData.y, pData.skin + '_head');
          head.setOrigin(0.5, 0.5);
          head.baseRadius = r;
          head.setRadius = function (newRadius) {
            const factor = SKIN_HEAD_FACTOR * skinTweak(skinOf(this), 'head');
            this.setScale(skinScaleFor(this, newRadius, factor));
          };
          head.setRadius(r);
          head.setFillStyle = function () { };
          head.setStrokeStyle = function () { };
          head.isImageSkin = true;
          head.isNeon = false;
        } else {
            // SISTEMA PROCEDURALE DI BASE (Per i colori HEX)
            let finalColor = headColor;
            let isNeon = false;

            if (pData.skin && pData.skin.startsWith('#')) {
                finalColor = Phaser.Display.Color.HexStringToColor(pData.skin).color;
            } else if (pData.skin && pData.skin.includes('neon')) {
                isNeon = true;
            }

            head = this.add.circle(pData.x, pData.y, r, finalColor);
            head.baseRadius = r;
            head.setRadius = function (newRadius) {
                this.setScale(newRadius / this.baseRadius);
            };

            if (isNeon) {
                head.setStrokeStyle(4, finalColor, 1);
                head.setFillStyle(0x050505, 0.9);
                head.glow = this.add.circle(0, 0, r * 1.5, finalColor, 0.3);
                head.glow.setBlendMode(Phaser.BlendModes.ADD);
                head.glow.setDepth(9);
            } else {
                head.setStrokeStyle(2, 0xFFFFFF, 0.8);
            }

            head.isImageSkin = false;
            head.isNeon = isNeon;
        }

        // UI text e Logo sopra il serpente
        const displayName = (id === this.myId) ? 'YOU' : (pData.tag || pData.name || (pData.wallet ? pData.wallet.substring(0, 5) : 'DEV'));
        head.nameText = this.add.text(pData.x, pData.y - 40, displayName, {
          fontSize: '14px', fill: '#ffffff', fontFamily: 'Courier New', stroke: '#000000', strokeThickness: 3
        }).setOrigin(0.5).setDepth(20);

        head.scoreText = this.add.text(pData.x, pData.y - 25, formatSolLabel(0), {
          fontSize: '12px', fill: '#39FF14', fontFamily: 'Courier New', fontStyle: 'bold', stroke: '#000000', strokeThickness: 3
        }).setOrigin(0.5).setDepth(20);

        head.logoText = this.add.text(pData.x, pData.y, 'V', {
          fontSize: '24px', fill: '#050505', fontFamily: 'Courier New', fontStyle: 'bold'
        }).setOrigin(0.5).setDepth(15);

        // Barra Cash Out
        head.cashoutBarBg = this.add.rectangle(pData.x, pData.y - 60, 60, 10, 0x000000, 0.8).setDepth(20).setVisible(false);
        head.cashoutBarFill = this.add.rectangle(pData.x - 28, pData.y - 60, 0, 6, 0xFFD700, 1).setDepth(21).setVisible(false).setOrigin(0, 0.5);

        // Setup proprietà custom
        head.bodySegments = [];
        head.playerId = id; // Importante per i nemici

        // --- NUOVO: OCCHI ---
        // Creiamo due occhi bianchi e due pupille nere
        // Li salviamo dentro l'oggetto 'head' per muoverli insieme
        head.leftEye = this.add.circle(0, 0, pData.radius * 0.35, 0xFFFFFF);
        head.rightEye = this.add.circle(0, 0, pData.radius * 0.35, 0xFFFFFF);
        head.leftPupil = this.add.circle(0, 0, pData.radius * 0.15, 0x000000);
        head.rightPupil = this.add.circle(0, 0, pData.radius * 0.15, 0x000000);

        if (head.isImageSkin) {
          head.leftEye.setVisible(false);
          head.rightEye.setVisible(false);
          head.leftPupil.setVisible(false);
          head.rightPupil.setVisible(false);
          head.logoText.setVisible(false);
        }

        // Ordine di profondità: Corpo(-1) -> Testa(0) -> Occhi(1) -> Pupille(2)
        head.setDepth(10); // Testa sopra il corpo
        head.leftEye.setDepth(11);
        head.rightEye.setDepth(11);
        head.leftPupil.setDepth(12);
        head.rightPupil.setDepth(12);

        if (id === this.myId) {
          this.myPlayer = head;
          playerRef = this.myPlayer;
          if (this.cameras && this.cameras.main) this.cameras.main.startFollow(this.myPlayer);
        } else {
          this.otherPlayers.add(head);
          playerRef = head;
        }
      }

      // --- FASE 2: AGGIORNAMENTO POSIZIONE E GRAFICA ---

      // --- AUDIO TRIGGERS (Only for my player) ---
      if (id === this.myId && this.synth) {
        // BOOST
        if (pData.isBoosting) {
          if (!playerRef.wasBoosting) this.synth.startBoost();
          playerRef.wasBoosting = true;
        } else {
          if (playerRef.wasBoosting) this.synth.stopBoost();
          playerRef.wasBoosting = false;
        }

        // EAT
        let newScore = pData.score || 0;
        let oldScore = playerRef.lastScore || 0;
        let newMass = pData.mass || 0;
        let oldMass = playerRef.lastMass || 0;
        // Il cibo base da' massa e 0 score (+1, o +2 col multiplier): la vecchia
        // soglia "> oldMass + 1" lo mancava sempre di un soffio, percio' suonava
        // solo raccogliendo i drop. Ora: drop -> blip pieno, cibo base -> blip soft.
        if (newScore > oldScore) {
          this.synth.playEat();
        } else if (newMass > oldMass) {
          this.synth.playEat(true);
        }
        playerRef.lastScore = newScore;
        playerRef.lastMass = newMass;

        // POWERUP: suoni legati alla tasca (non piu' agli effetti).
        // - Raccolta (tasca vuota -> piena): breve blip di conferma.
        // - Attivazione col tasto E (tasca piena -> vuota): "power charging".
        let currentEffects = pData.activeEffects || {};
        const newHeld = pData.heldPowerup || null;
        const oldHeld = playerRef.heldPowerup || null;
        if (!oldHeld && newHeld) {
          this.synth.playEat(); // conferma raccolta
        } else if (oldHeld && !newHeld) {
          this.synth.playPowerup(); // attivazione (charging/reload)
        }
        playerRef.lastEffects = { ...currentEffects };

        // PowerUp in tasca (da attivare col tasto E)
        playerRef.heldPowerup = newHeld;
      }

      // Il portatore della frenesia e' marcato per tutti: senza, il minuto
      // sarebbe un vantaggio invisibile e nessuno saprebbe chi inseguire.
      const eIlPortatore = !!this.frenzyPortatoreId && id === this.frenzyPortatoreId;
      if (eIlPortatore && !playerRef.frenzyAura) {
        playerRef.frenzyAura = this.add.circle(pData.x, pData.y, pData.radius * 2.2, 0xFFFFFF, 0.18)
          .setDepth(9)
          .setBlendMode(Phaser.BlendModes.ADD);
        playerRef.frenzyAura.setStrokeStyle(3, 0xFFFFFF, 0.9);
      }
      if (playerRef.frenzyAura) {
        playerRef.frenzyAura.setVisible(eIlPortatore);
        if (eIlPortatore) {
          playerRef.frenzyAura.x = pData.x;
          playerRef.frenzyAura.y = pData.y;
          playerRef.frenzyAura.setRadius(pData.radius * (2.0 + 0.25 * Math.sin(Date.now() / 120)));
        }
      }

      // 1. Aggiorna Testa
      playerRef.x = pData.x;
      playerRef.y = pData.y;
      playerRef.setRadius(pData.radius);
      
      // Update glow
      if (playerRef.glow) {
          playerRef.glow.x = pData.x;
          playerRef.glow.y = pData.y;
          playerRef.glow.setScale(pData.radius / playerRef.baseRadius);
      }
      
      // Seguiamo rigorosamente l'angolo del server per evitare vibrazioni e bug
      playerRef.rotation = pData.angle;

      // Aggiorna posizione testi e logo
      if (playerRef.nameText) {
        playerRef.nameText.x = pData.x;
        playerRef.nameText.y = pData.y - (pData.radius + 25);
      }
      if (playerRef.scoreText) {
        playerRef.scoreText.setText(formatSolLabel(pData.score || 0));
        playerRef.scoreText.x = pData.x;
        playerRef.scoreText.y = pData.y - (pData.radius + 10);
      }
      if (playerRef.logoText) {
        playerRef.logoText.x = pData.x;
        playerRef.logoText.y = pData.y;
      }

      // Cashout visual
      playerRef.isCashingOut = pData.isCashingOut;
      if (pData.isCashingOut) {
        if (playerRef.cashoutBarBg) {
          playerRef.cashoutBarBg.x = pData.x;
          playerRef.cashoutBarBg.y = pData.y - (pData.radius + 45);
          playerRef.cashoutBarBg.setVisible(true);

          playerRef.cashoutBarFill.x = pData.x - 28;
          playerRef.cashoutBarFill.y = pData.y - (pData.radius + 45);
          playerRef.cashoutBarFill.width = 56 * Math.min(1, pData.cashoutProgress || 0);
          playerRef.cashoutBarFill.setVisible(true);
        }
      } else {
        if (playerRef.cashoutBarBg) {
          playerRef.cashoutBarBg.setVisible(false);
          playerRef.cashoutBarFill.setVisible(false);
        }
      }

      // Effetto Illuminazione Boost (Testa) e Gestione Camera (Solo per MyPlayer)
      const displayColor = pData.color;
      let headAlpha = pData.isGhost ? 0.4 : 1;

      if (pData.isBoosting) {
        playerRef.setFillStyle(displayColor, headAlpha);
        playerRef.setStrokeStyle(6, displayColor, headAlpha);
      } else {
        playerRef.setFillStyle(displayColor, headAlpha);
        playerRef.setStrokeStyle(0);
      }

      // --- CAM UPDATE ---
      if (id === this.myId) {
        const now = Date.now();
        const isVisionActive = !!pData.activeEffects
          && ancoraAttivo(pData.activeEffects.visionEndTime, this.scartoOrologio, now);

        // Target zoom: 0.5 se VEDO LONTANO (Vision buff), altrimenti calcolato sulla mole 
        let targetZoom = 1.0;
        if (isVisionActive) {
          targetZoom = 0.5; // Zoom Out potente
        } else {
          // Zoom dinamico base basato sul raggio (diventa più grande -> vede di più)
          targetZoom = Phaser.Math.Clamp(1.2 - (pData.radius / 100), 0.35, 1.2);
        }

        // Compensazione per schermi piccoli: con lo zoom pensato per il desktop,
        // su un telefono in orizzontale si vedrebbe una fetta di mappa molto piu'
        // stretta (e si morirebbe per nemici arrivati da fuori campo). Riduciamo
        // lo zoom in proporzione all'altezza della viewport.
        // Riferimento 720px: da desktop il fattore resta 1 (nulla cambia rispetto
        // a prima), sotto quella soglia la visuale si allarga progressivamente.
        // `/ RENDER_SCALE`: i 720px sono una misura da occhio umano, e
        // `scale.height` ora e' in pixel fisici.
        const screenFactor = Phaser.Math.Clamp((this.scale.height / RENDER_SCALE) / 720, 0.55, 1);
        targetZoom *= screenFactor;

        // Il gioco misura in pixel fisici, quindi lo zoom va moltiplicato per
        // la stessa scala: senza, il mondo apparirebbe grande la meta'. E' qui
        // che i power-up guadagnano davvero i pixel — stessa dimensione a
        // schermo, RENDER_SCALE volte i pixel per disegnarli.
        targetZoom *= RENDER_SCALE;

        // Interpolazione lineare fluida dello zoom
        this.cameras.main.zoom += (targetZoom - this.cameras.main.zoom) * 0.05;
      }

      // 2. Aggiorna Occhi (Matematica per ruotarli)
      // Devono essere posizionati davanti alla testa rispetto all'angolo di movimento
      const angle = pData.angle; // Angolo in radianti dal server
      const offsetDist = pData.radius * 0.6; // Distanza dal centro
      const eyeSpacing = 0.3; // Distanza angolare tra gli occhi

      // Occhio Sinistro
      playerRef.leftEye.x = pData.x + Math.cos(angle - eyeSpacing) * offsetDist;
      playerRef.leftEye.y = pData.y + Math.sin(angle - eyeSpacing) * offsetDist;
      playerRef.leftEye.setRadius(pData.radius * 0.35);

      // Occhio Destro
      playerRef.rightEye.x = pData.x + Math.cos(angle + eyeSpacing) * offsetDist;
      playerRef.rightEye.y = pData.y + Math.sin(angle + eyeSpacing) * offsetDist;
      playerRef.rightEye.setRadius(pData.radius * 0.35);

      // Pupille (leggermente più avanti degli occhi per effetto "sguardo")
      const pupilOffset = pData.radius * 0.75;
      playerRef.leftPupil.x = pData.x + Math.cos(angle - eyeSpacing) * pupilOffset;
      playerRef.leftPupil.y = pData.y + Math.sin(angle - eyeSpacing) * pupilOffset;
      playerRef.leftPupil.setRadius(pData.radius * 0.15);

      playerRef.rightPupil.x = pData.x + Math.cos(angle + eyeSpacing) * pupilOffset;
      playerRef.rightPupil.y = pData.y + Math.sin(angle + eyeSpacing) * pupilOffset;
      playerRef.rightPupil.setRadius(pData.radius * 0.15);


      // 3. Aggiorna Corpo (Snake Body)
      if (pData.history && pData.history.length > 0) {
        // MUST BE 1. Se il server conta tutti i punti per le collisioni,
        // nasconderli sul client crea "collisioni fantasma" invisibili.
        // Per le skin immagine si salta qualche punto, ma quanti lo decide la
        // larghezza dello sprite: vedi passoSegmenti().
        let gap = 1;
        if (playerRef.isImageSkin) {
          const p0 = pData.history[0];
          const p1 = pData.history[1] || p0;
          const distanzaPunti = Phaser.Math.Distance.Between(p0.x, p0.y, p1.x, p1.y) || 7.2;
          gap = passoSegmenti(pData.skin, pData.radius || 20, distanzaPunti);
        }
        let segmentIndex = 0;
        let startIndex = playerRef.isImageSkin ? gap : 0;

        for (let i = startIndex; i < pData.history.length; i += gap) {
          const point = pData.history[i];

          let texKey = pData.color; // fallback
          if (playerRef.isImageSkin) {
            // L'ultimo segmento visualizzabile usa la coda
            if (i + gap >= pData.history.length) {
              texKey = pData.skin + '_tail';
            } else {
              texKey = pData.skin + '_body';
            }
          }

          // Crea segmento se manca
          if (!playerRef.bodySegments[segmentIndex]) {
            let seg;
            if (playerRef.isImageSkin) {
              seg = this.add.sprite(point.x, point.y, texKey);
              seg.baseRadius = pData.radius;
              seg.setRadius = function (newRadius) {
                const isTail = this.texture && this.texture.key && this.texture.key.endsWith('_tail');
                const part = isTail ? 'tail' : 'body';
                const base = isTail ? SKIN_TAIL_FACTOR : SKIN_BODY_FACTOR;
                const factor = base * skinTweak(skinOf(this), part);
                this.setScale(skinScaleFor(this, newRadius, factor));
              };
              seg.setFillStyle = function () { };
              seg.setStrokeStyle = function () { };
              seg.setAlpha(1.0);
            } else {
              let finalColor = pData.color;
              if (playerRef.isNeon && pData.skin) {
                 if (pData.skin.startsWith('#')) {
                   finalColor = Phaser.Display.Color.HexStringToColor(pData.skin).color;
                 } else {
                   if (pData.skin === 'BinarySnake') finalColor = 0x39FF14;
                   else if (pData.skin === 'singularity') finalColor = 0x9D00FF;
                   else if (pData.skin === 'Cybersnake') finalColor = 0x00FFFF;
                   else finalColor = 0xFFD700;
                 }
              }

              seg = this.add.circle(point.x, point.y, pData.radius, finalColor);
              seg.baseRadius = pData.radius;
              seg.segColor = finalColor;
              seg.setRadius = function (newRadius) {
                this.setScale(newRadius / this.baseRadius);
              };
              seg.setAlpha(0.8);
            }

            // La testa è depth 10, i segmenti vanno a scendere.
            seg.setDepth(5 - (segmentIndex * 0.01));
            playerRef.bodySegments.push(seg);
          }

          // Aggiorna segmento
          const seg = playerRef.bodySegments[segmentIndex];

          if (playerRef.isImageSkin) {
            if (seg.texture && seg.texture.key !== texKey) {
              seg.setTexture(texKey);
            }
            if (texKey.endsWith('_tail')) {
              seg.setOrigin(0.8, 0.5);
            } else {
              seg.setOrigin(0.5, 0.5);
            }
          }

          seg.x = point.x;
          seg.y = point.y;
          seg.setRadius(pData.radius);
          seg.setVisible(true);

          if (playerRef.isImageSkin) {
            if (segmentIndex === 0) {
              if (seg.x === playerRef.x && seg.y === playerRef.y) {
                seg.rotation = playerRef.rotation;
              } else {
                seg.rotation = Phaser.Math.Angle.Between(seg.x, seg.y, playerRef.x, playerRef.y);
              }
            } else {
              let prevPoint = playerRef.bodySegments[segmentIndex - 1];
              if (seg.x === prevPoint.x && seg.y === prevPoint.y) {
                seg.rotation = prevPoint.rotation;
              } else {
                seg.rotation = Phaser.Math.Angle.Between(seg.x, seg.y, prevPoint.x, prevPoint.y);
              }
            }
            seg.setAlpha(pData.isGhost ? 0.4 : 1.0);
          } else {
            // Effetto Illuminazione Procedurale (Neon / Boost)
            let bodyAlpha = pData.isGhost ? 0.2 : (pData.isBoosting ? 1 : 0.6);
            let strokeAlpha = pData.isGhost ? 0.1 : (pData.isBoosting ? 0.9 : 0.5);

            if (playerRef.isNeon) {
                seg.setFillStyle(0x050505, bodyAlpha);
                seg.setStrokeStyle(pData.isBoosting ? 4 : 2, seg.segColor, strokeAlpha);
            } else {
                seg.setFillStyle(seg.segColor, bodyAlpha);
                if (pData.isBoosting) {
                  seg.setStrokeStyle(2, 0xFFFFFF, strokeAlpha);
                } else {
                  seg.setStrokeStyle(0);
                }
            }
          }

          segmentIndex++;
        }

        // Nascondi segmenti extra
        for (let j = segmentIndex; j < playerRef.bodySegments.length; j++) {
          playerRef.bodySegments[j].setVisible(false);
        }
      }
    });

    // Pulizia Disconnessi
    const currentChildren = this.otherPlayers.getChildren();
    [...currentChildren].forEach((otherPlayer) => {
      if (!playersData[otherPlayer.playerId]) {
        // Fuori vista o disconnesso: si butta anche la scia ricostruita, cosi'
        // al rientro si riparte dal corpo intero che il server rimandera'.
        this.bodies.delete(otherPlayer.playerId);
        // Distruggi parti del corpo ed occhi
        if (otherPlayer.frenzyAura) otherPlayer.frenzyAura.destroy();
        if (otherPlayer.bodySegments) otherPlayer.bodySegments.forEach(s => s.destroy());
        if (otherPlayer.leftEye) otherPlayer.leftEye.destroy();
        if (otherPlayer.rightEye) otherPlayer.rightEye.destroy();
        if (otherPlayer.leftPupil) otherPlayer.leftPupil.destroy();
        if (otherPlayer.rightPupil) otherPlayer.rightPupil.destroy();
        if (otherPlayer.nameText) otherPlayer.nameText.destroy();
        if (otherPlayer.scoreText) otherPlayer.scoreText.destroy();
        if (otherPlayer.logoText) otherPlayer.logoText.destroy();

        otherPlayer.destroy();
      }
    });
  }

}